import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import '../App.css';

const getWorksPanels = (nightMode) => [
  {
    id: 'professional',
    to: '/works/professional',
    eyebrow: '',
    titleLines: ['PROFESSIONAL', 'EXPERIENCE'],
    gradient: 'linear-gradient(135deg, rgba(54, 52, 94, 0.98) 0%, rgba(26, 24, 54, 0.92) 45%, rgba(16, 14, 34, 0.92) 100%)',
    accent: '#343058',
    edge: 'linear-gradient(135deg, rgba(188, 170, 255, 0.55), rgba(120, 140, 255, 0.22))',
  },
  {
    id: 'projects',
    to: '/works/projects',
    eyebrow: '',
    titleLines: ['SIDE', 'PROJECTS'],
    gradient: 'linear-gradient(135deg, #ffebf5 0%, #ffd9ed 45%, #ffc9e5 100%)',
    accent: '#ff8fc7',
    edge: 'linear-gradient(135deg, rgba(255, 143, 199, 0.5), rgba(255, 217, 237, 0.2))',
  },
];

const WorksPage = ({ nightMode, setNightMode }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const worksPanels = useMemo(() => getWorksPanels(nightMode), [nightMode]);

  const getPaneState = (panelId) => {
    if (!hovered) return '';
    return hovered === panelId ? 'is-active' : 'is-muted';
  };

  return (
    <div className={`works-page ${nightMode ? 'night' : ''}`}>
      {/* Floating Particles Background */}
      <div className="works-particles">
        {useMemo(() => [...Array(15)].map((_, i) => {
          const randomX = Math.random() * 100;
          const randomY = Math.random() * 100;
          const randomDelay = Math.random() * 3;
          const randomDuration = 4 + Math.random() * 3;

          return (
            <motion.div
              key={i}
              className="works-particle"
              style={{
                left: `${randomX}%`,
                top: `${randomY}%`,
              }}
              animate={{
                y: [0, -30, 0],
                x: [0, Math.sin(i) * 20, 0],
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: randomDuration,
                repeat: Infinity,
                delay: randomDelay,
                ease: "easeInOut",
              }}
            />
          );
        }), [])}
      </div>

      <div className="works-nav">
        <motion.button
          className="works-back"
          onClick={() => navigate('/')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -6 }}
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </motion.button>

        <motion.button
          className="works-theme"
          onClick={() => setNightMode(!nightMode)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {nightMode ? '☀' : '🌙'}
        </motion.button>
      </div>

      <div className="works-stage">
        <div className="works-title">E X P E R I E N C E</div>
        <p className="works-subtitle">
          The part where learning turned into <span className="works-subtitle-accent">building.</span>
        </p>

        <div className="works-gallery" role="list">
          {worksPanels.map((panel, index) => (
            <motion.button
              type="button"
              key={panel.id}
              role="listitem"
              className={`works-card works-card--${panel.id} ${getPaneState(panel.id)}`}
              onMouseEnter={() => setHovered(panel.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => navigate(panel.to)}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.18 * index }}
              style={{
                '--card-gradient': panel.gradient,
                '--card-accent': panel.accent,
                '--card-edge': panel.edge,
              }}
            >
              <span className="works-card-outline" aria-hidden="true" />
              <span className="works-card-shadow" aria-hidden="true" />
              <div className="works-card-fill" aria-hidden="true" />
              <div className="works-card-overlay">
                <span className="works-card-eyebrow">{panel.eyebrow}</span>
                <div className="works-card-title">
                  {panel.titleLines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Navigation activeSection="work" onNavigate={() => {}} nightMode={nightMode} />
    </div>
  );
};

export default WorksPage;
